import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const TYPES = [
  { key: 'vertrag', label: 'Vertrag', desc: 'Klauseln, Fristen, Risiken' },
  { key: 'finanzbericht', label: 'Finanzbericht', desc: 'KPIs, Trends, Abweichungen' },
  { key: 'strategie', label: 'Strategie', desc: 'Kernthesen, Empfehlungen' },
  { key: 'marktbericht', label: 'Marktbericht', desc: 'Trends, Chancen, Wettbewerb' },
  { key: 'allgemein', label: 'Allgemein', desc: 'Summary, Key Facts, Aktion' },
];

function colorize(text) {
  if (!text) return '';
  return text
    .replace(/\[HOCH\]/g, '<span style="color:#f7768e;font-weight:600">[HOCH]</span>')
    .replace(/\[MITTEL\]/g, '<span style="color:#e0af68;font-weight:600">[MITTEL]</span>')
    .replace(/\[NIEDRIG\]/g, '<span style="color:#9ece6a;font-weight:600">[NIEDRIG]</span>')
    .replace(/^### (.+)$/gm, '<h4 style="color:#bb9af7;margin:12px 0 6px">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="color:var(--text-primary);margin:16px 0 8px;font-size:16px">$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

export default function Analyze() {
  const [params] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(params.get('doc') || '');
  const [directText, setDirectText] = useState('');
  const [mode, setMode] = useState(params.get('doc') ? 'doc' : 'doc');
  const [analyzeType, setAnalyzeType] = useState('allgemein');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const resultRef = useRef(null);

  useEffect(() => {
    fetch('/api/documents?page=1&page_size=100').then(r => r.ok ? r.json() : {}).then(d => setDocs(Array.isArray(d?.documents) ? d.documents : [])).catch(() => {});
  }, []);

  const startAnalysis = async () => {
    setLoading(true); setResult(''); setSaved(false);
    const url = mode === 'doc' ? `/api/workspace/${selectedDoc}/analyze` : '/api/workspace/analyze-text';
    const body = mode === 'doc' ? { analyze_type: analyzeType } : { text: directText, analyze_type: analyzeType };
    try {
      const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error(await r.text());
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let text = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            try {
              const j = JSON.parse(data);
              if (j.text) { text += j.text; setResult(text); }
            } catch {}
          }
        }
      }
    } catch (e) { setResult('Fehler: ' + e.message); }
    setLoading(false);
  };

  const saveAnalysis = async () => {
    if (!selectedDoc || mode !== 'doc') return;
    await fetch(`/api/workspace/${selectedDoc}/save-analysis`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ analyze_type: analyzeType, result }),
    }).catch(() => {});
    setSaved(true);
  };

  const copyResult = () => { navigator.clipboard.writeText(result); };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left */}
      <div style={{ width: 300, minWidth: 300, borderRight: '1px solid var(--border)', padding: 'var(--gap-md)', overflow: 'auto' }}>
        <h3 style={{ color: 'var(--text-primary)', fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 12 }}>KI-Analyse</h3>
        <div style={{ display: 'flex', gap: 4, marginBottom: 'var(--gap-md)' }}>
          {['doc', 'text'].map(m => (
            <button key={m} onClick={() => setMode(m)} style={{ flex: 1, background: mode === m ? 'rgba(187,154,247,0.2)' : 'var(--bg-tertiary)', border: `1px solid ${mode === m ? '#bb9af7' : 'var(--border)'}`, borderRadius: 'var(--border-radius)', padding: '6px 8px', color: mode === m ? '#bb9af7' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-xs)' }}>
              {m === 'doc' ? 'Dokument' : 'Text'}
            </button>
          ))}
        </div>

        {mode === 'doc' ? (
          <select value={selectedDoc} onChange={e => setSelectedDoc(e.target.value)} style={{ width: '100%', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '8px 10px', color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--gap-md)' }}>
            <option value="">Dokument waehlen...</option>
            {docs.map(d => <option key={d.id} value={d.id}>{d.title || d.filename}</option>)}
          </select>
        ) : (
          <textarea value={directText} onChange={e => setDirectText(e.target.value)} placeholder="Text hier eingeben..." rows={6} style={{ width: '100%', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '8px 10px', color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)', resize: 'vertical', outline: 'none', marginBottom: 'var(--gap-md)' }} />
        )}

        <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>Analyse-Typ</div>
        {TYPES.map(t => (
          <button key={t.key} onClick={() => setAnalyzeType(t.key)} style={{ display: 'block', width: '100%', textAlign: 'left', background: analyzeType === t.key ? 'rgba(187,154,247,0.15)' : 'var(--bg-secondary)', border: `1px solid ${analyzeType === t.key ? '#bb9af7' : 'var(--border)'}`, borderRadius: 'var(--border-radius)', padding: '10px 12px', marginBottom: 4, cursor: 'pointer' }}>
            <div style={{ color: analyzeType === t.key ? '#bb9af7' : 'var(--text-primary)', fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>{t.label}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>{t.desc}</div>
          </button>
        ))}

        <button onClick={startAnalysis} disabled={loading || (mode === 'doc' ? !selectedDoc : !directText.trim())} style={{ marginTop: 'var(--gap-md)', width: '100%', background: '#bb9af7', border: 'none', borderRadius: 'var(--border-radius)', padding: '10px 16px', color: '#1a1b26', fontWeight: 600, cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Analysiere...' : 'Analyse starten'}
        </button>
      </div>

      {/* Right */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--gap-md)' }}>
        {result ? (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {mode === 'doc' && selectedDoc && <button onClick={saveAnalysis} style={{ background: saved ? 'rgba(158,206,106,0.15)' : 'var(--bg-tertiary)', border: `1px solid ${saved ? '#9ece6a' : 'var(--border)'}`, borderRadius: 'var(--border-radius)', padding: '6px 12px', color: saved ? '#9ece6a' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>{saved ? 'Gespeichert' : 'Speichern'}</button>}
              <button onClick={copyResult} style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '6px 12px', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>Kopieren</button>
            </div>
            <div ref={resultRef} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius-lg)', padding: 24, color: 'var(--text-primary)', lineHeight: 1.7, fontSize: 'var(--font-size-sm)' }} dangerouslySetInnerHTML={{ __html: colorize(result) }} />
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)', fontSize: 'var(--font-size-base)' }}>
            {loading ? 'Analysiere...' : 'Dokument waehlen und Analyse starten'}
          </div>
        )}
      </div>
    </div>
  );
}
